/**
 * Every result this session, newest first — the mockup's Games tab.
 *
 * This is what somebody opens to answer "did that one get logged?", which is
 * asked far more often than it is convenient to scroll to the bottom of the
 * entry screen. It had no home of its own before.
 *
 * Rule L-2 — results this device is holding but has not sent are shown,
 * marked, rather than vanishing until there is signal. They carry no Undo:
 * there is nothing on the server to undo yet, and offering one would suggest
 * the queue can be edited from here when it cannot.
 */

import { useState } from "react";
import { api, type Game, type ScoreOption } from "../api.js";
import type { SyncOp } from "../sync/queue.js";
import { EditGame } from "./EditGame.js";

export function GamesList({
  games,
  queued,
  nameOf,
  targets,
  optionsByTarget,
  onChanged,
}: {
  games: Game[];
  queued: SyncOp[];
  nameOf: (id: string) => string;
  targets: number[];
  optionsByTarget: Record<string, ScoreOption[]>;
  onChanged: () => Promise<void>;
}): React.JSX.Element {
  const newest = games[games.length - 1]?.id;
  const [editing, setEditing] = useState<Game | null>(null);

  if (editing) {
    return (
      <EditGame
        game={editing}
        nameOf={nameOf}
        targets={targets}
        optionsByTarget={optionsByTarget}
        onCancel={() => setEditing(null)}
        onSaved={async () => {
          setEditing(null);
          await onChanged();
        }}
      />
    );
  }

  if (games.length === 0 && queued.length === 0) {
    return (
      <section className="block">
        <h3 className="card-head">Games</h3>
        <p>No games yet. Log one from the Play tab — tap the winning side, then their opponents&apos; score.</p>
      </section>
    );
  }

  return (
    <>
      <div className="eyebrow">
        {games.length} {games.length === 1 ? "result" : "results"}
      </div>

      <section className="block">
        {queued.map((op) => {
          const p = op.payload as {
            sideA: string[];
            sideB: string[];
            sideAScore: number;
            sideBScore: number;
          };
          return (
            <div key={op.clientUuid} className="grow provisional">
              <div className="gline">
                <span className="nm">{p.sideA.map(nameOf).join(" & ")}</span>
                <span className="sc num">{p.sideAScore}</span>
              </div>
              <div className="gline">
                <span className="nm">{p.sideB.map(nameOf).join(" & ")}</span>
                <span className="sc num">{p.sideBScore}</span>
              </div>
              <span className="provisional-flag">on this phone, not sent yet</span>
            </div>
          );
        })}

        {games
          .slice()
          .reverse()
          .map((game, index) => {
            const aWon = game.winnerSide === "A";
            return (
              <div key={game.id} className="grow">
                <div className="grow-head">
                  <span className="muted small">
                    Game {games.length - index} · to {game.targetScore}
                  </span>
                  <span className="row">
                    {/* FR-ENTRY-10 — any game can be corrected, not just the
                        last. Until this existed, a wrong score entered three
                        games ago could only be looked at. */}
                    <button className="mini" onClick={() => setEditing(game)}>
                      Edit
                    </button>
                    {/* FR-ENTRY-9 — the most recent game also stays one tap
                        from being removed outright. */}
                    {game.id === newest ? (
                      <button
                        className="mini"
                        onClick={() => {
                          void api.undoGame(game.id).then(onChanged);
                        }}
                      >
                        Undo
                      </button>
                    ) : null}
                  </span>
                </div>
                <div className={aWon ? "gline win" : "gline"}>
                  <span className="nm">{game.sideA.map(nameOf).join(" & ")}</span>
                  <span className="sc num">{game.sideAScore}</span>
                </div>
                <div className={aWon ? "gline" : "gline win"}>
                  <span className="nm">{game.sideB.map(nameOf).join(" & ")}</span>
                  <span className="sc num">{game.sideBScore}</span>
                </div>
              </div>
            );
          })}
      </section>

      <p className="note">
        Any result can be corrected; the most recent can also be undone
        outright. Every change is recorded against the session and shown on the
        End screen.
      </p>
    </>
  );
}
